import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DEFAULTS } from "../../../Constants/Formatting.js";
import { LoginInput } from "../../../Components/Input/Input";
import styled from "styled-components";
import "@fontsource/montserrat";
import { FRONTEND_LINKS } from "../../../Constants/FrontendLinks.js";
import { useEndpointProvider } from "../../../Providers/EndpointProvider.js";
import * as MdIcons from 'react-icons/md';
import { InfoMessage } from "../../../Constants/StyledComponents";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider";
import { useWorkspacesProvider } from "../../../Providers/WorkspacesProvider"
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont";
import bgImg from "../Login/bgImg.png";
import darkLogoImg from "../Login/moberg-logo-dark.png";
import lightLogoImg from "../Login/moberg-logo-light.png"
import { useMobergTheme } from "../../../Providers/MobergThemeProvider"

// Email validation function
const isValidEmail = (email) => {
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	
	if (!email || typeof email !== 'string') return false;
	if (email.length > 254) return false;
	
	const localPart = email.split('@')[0];
	if (localPart.length > 64) return false;

	if (email.includes('..')) return false;
	
	return emailRegex.test(email);
};

const ForgotPassword = (props) => {
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const selectedWorkspace = useWorkspacesProvider()
	const { colorScheme } = useMobergTheme()
	const logoImg = colorScheme === "dark" ? lightLogoImg : darkLogoImg
	const [email, setEmail] = useState('');
	const [disableSendEmailButton, setDisableSendEmailButton] = useState(true)
	const [showEmailInfoMessage, setShowEmailInfoMessage] = useState(false)
	const [emailError, setEmailError] = useState('')
	const [successMessage, setSuccessMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	let navigate = useNavigate();

	useEffect(() => {
		// auth.logout()
		props.setSidebar(false)
		props.setNavPage(false)
	}, [])


	const resetPassword = useCallback((email) => {
		let body = {
			email: email
		}

		return endpointProvider.post(LINKS.LOGIN.SEND_RESET_PASSWORD_EMAIL, body)
	}, [endpointProvider])

	function handleSendEmail() {
		setDisableSendEmailButton(true)
		setShowEmailInfoMessage(true)
	}

	function resetPage() {
		setDisableSendEmailButton(false)
		setShowEmailInfoMessage(false)
		setEmailError('')
	}

	function handleResetPassword(e) {
		if (e) {
			e.preventDefault()
		}

		if (!isValidEmail(email)) {
			setEmailError('Please enter a valid email address.')
			return
		}

		setSuccessMessage('');
		setErrorMessage('');
		handleSendEmail()
		resetPassword(email.toLowerCase()).then(() => {
			setSuccessMessage(`If an account is associated with ${email}, you'll receive a password reset link shortly.`);
			setShowEmailInfoMessage(false);
			setDisableSendEmailButton(false);
			setEmail('');
		}).catch(() => {
			setErrorMessage('Something went wrong. Please try again later.');
			setShowEmailInfoMessage(false);
			setDisableSendEmailButton(false);
		})
	}

	function handleBackToLogin() {
		navigate(FRONTEND_LINKS.MAIN);
	}

	useEffect(() => {
		if (email.length > 0 && isValidEmail(email)) {
			setDisableSendEmailButton(false)
			setEmailError('')
		} else {
			setDisableSendEmailButton(true)
			if (email.length > 0 && !isValidEmail(email)) {
				setEmailError('Please enter a valid email address.')
			} else {
				setEmailError('')
			}
		}
	}, [email])

	return (
		<ClinicalWrapper>
			<CenteredLoginBox>
			
				<LoginBox style={{ boxShadow: "var(--modal-box-shadow)", background: "var(--color-surface-panel)", borderRadius: 4, minWidth: 350, padding: 32 }}>
				<img src={logoImg} alt="Moberg Analytics" style={{ width: '90px', alignSelf: "flex-start", marginBottom: 16 }} />
						<LoginSubheading style={{ fontFamily: MobergFontFamily.HEADER, letterSpacing: 1, fontSize: "24px", marginBottom: 8 }}>Reset password</LoginSubheading>
						<p style={{ fontSize: "14px", lineHeight: "24px", color: "var(--text-secondary-color)", marginBottom: 24 }}>Enter your email to reset your password.</p>
				{successMessage && (
					<div style={{ marginBottom: 16, wordBreak: 'break-all', overflowWrap: 'anywhere', padding: 12, border: "1px solid var(--color-accent-interactive)", borderRadius: 4, fontSize: "12px", color: "var(--color-text-primary)" }}>{successMessage}</div>
				)}
				{errorMessage && (
					<div style={{ marginBottom: 16, color: 'var(--color-danger-text)', border: '1px solid var(--color-danger-text)', wordBreak: 'break-all', overflowWrap: 'anywhere', padding: 12, fontSize: "12px", borderRadius: 4 }}>{errorMessage}</div>
				)}
				<CenterSection>
						<LoginForm>
							<LoginInputBox>
								<LoginLabel htmlFor="emailInput">Email<span style={{ color: 'red' }}>*</span></LoginLabel>
								<input 
									id="emailInput" 
									type="email" 
									placeholder="Email" 
									value={email || ""} 
									onChange={e => setEmail(e.target.value)} 
									autoComplete="username" 
									style={{
										borderColor: emailError ? 'var(--color-danger-text)' : 'var(--color-border-default)'
									}}
								/>
								{emailError && (
									<ErrorMessage>{emailError}</ErrorMessage>
								)}
							</LoginInputBox>
						</LoginForm>
					</CenterSection>
					<BottomSection>
						<LoginButton 
							type="button" 
							onClick={handleResetPassword}
							disabled={disableSendEmailButton || showEmailInfoMessage}
							style={{ 
								opacity: (disableSendEmailButton || showEmailInfoMessage) ? "0.3" : "1", 
								cursor: (disableSendEmailButton || showEmailInfoMessage) ? "default" : "pointer",
								pointerEvents: (disableSendEmailButton || showEmailInfoMessage) ? "none" : "all"
							}}
						>
							{showEmailInfoMessage ? (
								<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
									<div style={{ 
										width: "16px", 
										height: "16px", 
										border: "2px solid #fff", 
										borderTop: "2px solid transparent", 
										borderRadius: "50%", 
										animation: "spin 1s linear infinite",
										marginRight: "8px"
									}}></div>
									Sending email...
								</div>
							) : (
								"Send email"
							)}
						</LoginButton>
						<BackButton onClick={handleBackToLogin}>
							<MdIcons.MdOutlineArrowRightAlt 
								size={20} 
								style={{ 
									marginRight: "8px",
									transform: "rotate(180deg)"
								}} 
							/>
							Back to sign in
						</BackButton>
					</BottomSection>
				</LoginBox>
			</CenteredLoginBox>
		</ClinicalWrapper>
	);
};

const ClinicalWrapper = styled.div`
	background-image: url(${bgImg});
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	background-color: var(--color-surface-primary);
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0);
		[data-color-scheme="dark"] & {
			background: rgba(0, 0, 0, 0.75);
		}
	}

	& > * {
		position: relative;
		z-index: 1;
	}
`;

const CenteredLoginBox = styled.div`
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: center;
	justify-self: center;
	height: auto;
	width: 100%;
	max-width: 450px;
	min-width: 400px;
	padding: 0 20px;
	margin: 0 auto;
	left: 0;
	top: 0;

	@media (max-width: 768px) {
		width: 90%;
		min-width: 280px;
		max-width: 400px;
	}

	@media (max-width: 480px) {
		width: 95%;
		min-width: 260px;
		max-width: 350px;
		padding: 0 10px;
	}
`;

const LoginBox = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
`;


const CenterSection = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 0;
`;

const BottomSection = styled.div`
	width: 100%;
	padding-bottom: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	@media (max-width: 600px) {
		padding-bottom: 32px;
	}
`;

const LoginSubheading = styled.h2`
	margin: 0 0 24px 0;
	letter-spacing: 1px;
	color: var(--color-text-primary);
`;

const LoginForm = styled.form`
	width: 100%;
	max-width: 100%;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 24px;
	@media (max-width: 600px) {
		width: 100%;
		min-width: 0;
		gap: 20px;
	}
`;

const LoginInputBox = styled.div`
	position: relative;
	margin-bottom: 0;
	input {
		width: 100%;
		padding: 12px 40px 12px 12px;
		border: 1.5px solid var(--color-border-default);
		border-radius: 4px;
		font-size: 14px;
		font-family: inherit;
		outline: none;
		transition: border 0.2s;
		background: var(--color-input-bg);
		color: var(--color-text-primary);
		&:focus {
			border-color: var(--color-accent-interactive);
		}
		&::-ms-reveal {
			display: none;
		}
		&:-webkit-autofill,
		&:-webkit-autofill:hover,
		&:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px var(--color-input-bg) inset;
			-webkit-text-fill-color: var(--color-text-primary);
			caret-color: var(--color-text-primary);
			transition: background-color 5000s ease-in-out 0s;
		}
	}
`;

const LoginLabel = styled.label`
	display: block;
	font-size: 16px;
	color: var(--color-text-primary);
	font-weight: 500;
	margin-bottom: 6px;
	margin-left: 2px;
	font-family: inherit;
`;

const ErrorMessage = styled.div`
	color: var(--color-danger-text);
	font-size: 12px;
	margin-top: 4px;
	margin-left: 2px;
	font-family: inherit;
`;

const LoginButton = styled.button`
	width: 100%;
	padding: 12px 0;
	background: var(--color-accent-interactive);
	color: #fff;
	font-size: 16px;
	font-weight: 600;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	margin-top: 32px;
	transition: filter 0.2s;

	[data-color-scheme="dark"] & {
		background: #207DEA;
		color: #fff;
	}

	&:hover {
		filter: brightness(0.85);
	}
	&:disabled {
		background: var(--color-border-default);
		cursor: not-allowed;
	}
`;

const BackButton = styled.button`
	margin-top: 24px;
	display: flex;
	width: 100%;
	height: 40px;
	align-items: center;
	justify-content: center;
	padding: 8px 16px;
	border-radius: 4px;
	border: 1px solid var(--color-accent-interactive);
	background: transparent;
	color: var(--color-accent-interactive);
	font-weight: 500;
	font-size: 14px;
	transition: all 0.2s;
	cursor: pointer;
	align-self: center;
	
	&:hover {
		background: var(--color-accent-interactive);
		color: var(--color-hover-active-blue-text, #fff);
	}
`;


export default ForgotPassword;

